/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useState } from "react"
import { Modal, Radio, Button, Space, Row, Col } from "@duxton-web/ui"
import { useTranslation } from "common/utils/i18n"
import {
  AvailableStatusesText,
  availableStatusesNonRecurring,
  availableStatusesRecurring,
  availableStatusesMixed,
  availableStatusesUsage,
  availableStatuses,
} from "utils/item_helper"

export interface ItemAvailabilityModalProps {
  visible: boolean
  hasRecurring?: boolean
  hasNonRecurring?: boolean
  onClose(): void
  onConfirm(availableStatus: number): void | Promise<void>
}

export const ItemAvailabilityModal: FC<ItemAvailabilityModalProps> = ({
  visible,
  hasRecurring,
  hasNonRecurring,
  onClose,
  onConfirm,
}: ItemAvailabilityModalProps) => {
  const [value, setValue] = useState<number>(availableStatuses.AVAILABLE)

  const handleChange = (e: any) => {
    setValue(e.target.value)
  }

  const handleConfirm = async () => {
    await onConfirm(Number(value ?? 1))
    setValue(availableStatuses.AVAILABLE)
    onClose()
  }

  const handleOnClose = () => {
    onClose()
    setValue(availableStatuses.AVAILABLE)
  }

  let statuses: number[] = []

  if (hasRecurring && hasNonRecurring) {
    statuses = availableStatusesMixed
  } else {
    if (hasRecurring) statuses = availableStatusesRecurring
    if (hasNonRecurring) statuses = availableStatusesNonRecurring
  }

  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.ITEM_SELECTOR,
  )

  return (
    <Modal
      title={useTranslation("menu.modals.changeAvailability.title")}
      open={visible}
      onCancel={handleOnClose}
      width={460}
      zIndex={10000}
      footer={
        <Row justify="center" gutter={16}>
          <Col span={12}>
            <Button
              data-testid="btn-cancel"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleOnClose}
            >
              {useTranslation("menu.buttons.cancel")}
            </Button>
          </Col>
          <Col span={12}>
            <Button
              data-testid="btn-confirm"
              type="primary"
              style={{ width: "100%", justifyContent: "center" }}
              onClick={handleConfirm}
              disabled={value === null}
            >
              {useTranslation("menu.buttons.done")}
            </Button>
          </Col>
        </Row>
      }
    >
      <Radio.Group
        onChange={handleChange}
        value={value}
        style={{ marginTop: 16, marginBottom: 16 }}
      >
        <Space direction="vertical" size={14}>
          {statuses.map(availableStatus => (
            <Radio key={availableStatus} value={Number(availableStatus)}>
              {availableStatusesText[Number(availableStatus)]}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    </Modal>
  )
}
